/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { api } from "core/api";
import { Card, Button } from "core/components";
import {
  admin_item_inventory_path,
  admin_item_inventories_path,
  warehouse_inventory_admin_item_inventories_path,
  items_admin_item_inventories_path
} from "routes";
import ItemInventoryForm from "./ItemInventoryForm";
import ItemInventoryContext from "./ItemInventoryContext";
import { navigateTo } from "core/navigation";
import ItemInventoryProjectAllocation from "./ItemInventoryProjectAllocation";
import ItemInventoryHistory from "./ItemInventoryHistory";

type ItemInventoryViewProps = {
  warehouses: Array<Record<string, unknown>>;
  has_item_inventory_histories: boolean;
};

const ItemInventoryOverview = ({ warehouses, actions, adjustment_reasons, isItemsLoading, items, warehouse_id }) => {
  const [showItemInventoryModal, setShowItemInventoryModal] = React.useState(false);
  const { itemInventory, setItemInventory } = React.useContext(ItemInventoryContext);
  const [loading, setLoading] = React.useState(false);

  const getItemInventory = async (id) => {
    try {
      setLoading(true);
      let response = null;

      if (warehouse_id) {
        response = await api.get(warehouse_inventory_admin_item_inventories_path({ warehouse_id, item_id: itemInventory.id }));
        setItemInventory(response.data.inventory_item);
      } else {
        response = await api.get(admin_item_inventory_path(id));
        setItemInventory({ ...response.data });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Inventory...</span>
        </Spinner>
      </div>
    );
  };

  React.useEffect(() => {
    getItemInventory(itemInventory.id).catch(console.error);
  }, []);

  return (
    <Container fluid={true}>
      <Row>
        <Col>
          <Card>
            <Card.Header title="Current Inventory">
              <Button
                className="button--brand"
                title="Update Inventory"
                icon="edit"
                disabled={loading}
                onClick={() => {
                  setShowItemInventoryModal(!showItemInventoryModal);
                }}
              />
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center mb-1">
                <Col>
                  <div className="card card--completed">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Total</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.total_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="card card--on-going">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Available</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.available_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="card card--pending">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Allocated</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.reserved_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Header title="Project Allocation" />
            <Card.Body>
              <ItemInventoryProjectAllocation inventory_item={itemInventory} warehouse_id={warehouse_id} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <ItemInventoryForm
        warehouses={warehouses}
        actions={actions}
        warehouse_id={warehouse_id}
        adjustment_reasons={adjustment_reasons}
        inventoryItem={itemInventory}
        isItemsLoading={isItemsLoading}
        items={items}
        show={showItemInventoryModal}
        onClose={() => setShowItemInventoryModal(!showItemInventoryModal)}
        onSave={() => {
          setShowItemInventoryModal(!showItemInventoryModal);
          getItemInventory(itemInventory.id).catch(console.error);
        }}
      />
    </Container>
  );
};

const ItemInventoryWarehouse = ({ warehouses, warehouse_id, actions, adjustment_reasons, isItemsLoading, items, item_inventory }) => {
  const [showItemInventoryModal, setShowItemInventoryModal] = React.useState(false);
  const [itemInventory, setItemInventory] = React.useState(item_inventory);
  const [loading, setLoading] = React.useState(false);

  const getItemInventory = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(warehouse_inventory_admin_item_inventories_path({ warehouse_id: id, item_id: itemInventory.id }));
      setItemInventory(response.data.inventory_item);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Inventory...</span>
        </Spinner>
      </div>
    );
  };

  React.useEffect(() => {
    getItemInventory(warehouse_id).catch(console.error);
  }, [warehouse_id]);

  return (
    <Container fluid={true}>
      <Row>
        <Col>
          <Card>
            <Card.Header title="Current Inventory">
              <Button
                className="button--brand"
                title="Update Inventory"
                icon="edit"
                disabled={loading}
                onClick={() => {
                  setShowItemInventoryModal(!showItemInventoryModal);
                }}
              />
            </Card.Header>
            <Card.Body>
              <Row className="align-items-center mb-1">
                <Col>
                  <div className="card card--completed">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Total</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.total_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="card card--on-going">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Available</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.available_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="card card--pending">
                    <div className="card__content">
                      <div className="grid align-items-center mb-2">
                        <div className="tile">
                          <h5>Allocated</h5>
                        </div>
                      </div>
                      <h3>{loading ? loader() : (+itemInventory?.reserved_quantity || 0).toLocaleString("en-US", { minimumFractionDigits: 1 })}</h3>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Row>
          <Col>
            <Card>
              <Card.Header title="Project Allocation" />
              <Card.Body>
                <ItemInventoryProjectAllocation inventory_item={itemInventory} warehouse_id={warehouse_id} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Row>
      <ItemInventoryForm
        warehouses={warehouses}
        warehouse_id={warehouse_id}
        actions={actions}
        adjustment_reasons={adjustment_reasons}
        inventoryItem={itemInventory}
        isItemsLoading={isItemsLoading}
        items={items}
        show={showItemInventoryModal}
        onClose={() => setShowItemInventoryModal(!showItemInventoryModal)}
        onSave={() => {
          setShowItemInventoryModal(!showItemInventoryModal);
          getItemInventory(warehouse_id).catch(console.error);
        }}
      />
    </Container>
  );
};

const ItemInventoryView = ({
  warehouses,
  inventory_item,
  actions,
  adjustment_reasons,
  has_item_inventory_histories,
  warehouse_id
}: ItemInventoryViewProps): JSX.Element => {
  const [itemInventory, setItemInventory] = React.useState(inventory_item);
  const [isItemsLoading, setIsItemsLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);

  const getInventoryItems = async () => {
    try {
      setIsItemsLoading(true);
      const response = await api.get(items_admin_item_inventories_path({ format: "json", item_id: itemInventory.id }));
      setItems(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsItemsLoading(false);
    }
  };

  React.useEffect(() => {
    getInventoryItems();
  }, []);

  return (
    <React.StrictMode>
      <ItemInventoryContext.Provider value={{ itemInventory, setItemInventory }}>
        <Tabs className="card" selectedTabClassName="project-tab__link project-tab__link--active">
          <Container fluid={true}>
            <Row className="d-flex align-items-baseline">
              <Col>
                <TabList className="card__header" style={{ padding: 0 }}>
                  <Col className="d-flex justify-content-start">
                    <Tab className="project-tab__link" data-warehouse-id={null}>
                      Overview
                    </Tab>
                    <Tab className="project-tab__link" data-warehouse-id={null}>
                      Movement
                    </Tab>
                    {!warehouse_id &&
                      warehouses.map((warehouse) => {
                        return (
                          <Tab key={warehouse.id} data-warehouse-id={warehouse.id} className="project-tab__link">
                            {warehouse.name}
                          </Tab>
                        );
                      })}
                  </Col>
                </TabList>
              </Col>
              <Col className="d-flex justify-content-end">
                <Button
                  icon="arrow-left"
                  iconProps={{ className: "mr-3" }}
                  className="button--default mr-3"
                  title="Back"
                  onClick={() => {
                    navigateTo(admin_item_inventories_path());
                  }}
                />
              </Col>
            </Row>
            <TabPanel>
              <ItemInventoryOverview
                warehouses={warehouses}
                inventory_item={inventory_item}
                actions={actions}
                adjustment_reasons={adjustment_reasons}
                isItemsLoading={isItemsLoading}
                items={items}
                warehouse_id={warehouse_id}
              />
            </TabPanel>
            <TabPanel>
              <ItemInventoryHistory
                inventory_item={inventory_item}
                warehouse_id={warehouse_id}
                warehouses={warehouses}
                has_item_inventory_histories={has_item_inventory_histories}
              />
            </TabPanel>
            {!warehouse_id &&
              warehouses.map((warehouse) => {
                return (
                  <TabPanel key={warehouse.id}>
                    <ItemInventoryWarehouse
                      warehouses={warehouses}
                      warehouse_id={warehouse.id}
                      warehouse_inventory={inventory_item}
                      actions={actions}
                      adjustment_reasons={adjustment_reasons}
                      isItemsLoading={isItemsLoading}
                      items={items}
                      item_inventory={itemInventory}
                    />
                  </TabPanel>
                );
              })}
          </Container>
        </Tabs>
      </ItemInventoryContext.Provider>
    </React.StrictMode>
  );
};

export default ItemInventoryView;
